/**
 * Present Estimate — the rep's side of the customer document.
 *
 * Pressing Present does three things at once, and the order matters:
 *
 *   1. Build the document from the job as it stands right now
 *      (`buildPresentedDocument`, packages/core).
 *   2. Write it into `proposals.snapshot` as the row is created, already sent.
 *      From that instant the database will not let it change (ADR 0032).
 *   3. Hand back a link.
 *
 * Presenting does not send it. That is a second, separate press, and there are
 * three ways to make it: email it from here, if the rep has connected their
 * Gmail; copy the link into whatever they already use, which is still how a
 * text message happens; or Sign now on this device, the kitchen-table case that
 * needs no delivery at all.
 *
 * Which contract goes on the bottom is not asked at all — `contractKindFor` in
 * packages/core reads it off the trades on the estimates being offered, so a
 * repair quote gets the repair agreement and anything selling a replacement
 * gets the long one. See ADR 0037.
 */

import {
  HOME_SPECIFIC_SECTION,
  homeSpecificHeading,
  contractKindFor,
  documentComparesRoofs,
  documentSellsSeveralRoofs,
  isReplacementTrade,
  keyDifferentiatorPackages,
  packageShingleDifferentiator,
  addChartRow,
  chartRows,
  removeChartRow,
  retypeChartRow,
  tidyChartEdits,
  type ChartEdits,
  packetSummary,
  ROOFS_TOGETHER_NOTICE,
  sellsSeveralRoofs,
  estimateOptionLabel,
  estimateOptionLabels,
  type PackageCopyLine,
  distinguishNames,
  packageNameFor,
  colorCards,
  showFinancing,
  presentFinancingGap,
  type ColorCard,
  primaryPhone,
  buildPresentedDocument,
  documentPackages,
  isHomeSpecificSection,
  isWordingLine,
  packageBlocks,
  printsInPackage,
  packetOrder,
  mainEstimateOrder,
  upgradeSuitsRoofs,
  sectionKey,
  HOME_SPECIFIC_KEY,
  computeLadder,
  estimatesToOffer,
  formatMoney,
  toCents,
  jobAddress,
  paymentSchedule,
  priceUpgrade,
  squareCountLeaks,
  squareCountWarning,
  type SquareCountLeak,
  unfilledContractTokens,
  type Cents,
  type CompanyFacts,
  type ConnectedSender,
  type ContractValues,
  type DocumentSection,
  type Estimate,
  type EstimateItem,
  type EstimatePayment,
  type Job,
  type LadderTotals,
  type PackageInput,
  type PresentedDocument,
  type RoofLayout,
  type UpgradeInput,
  planTermsOf,
  planWontPrint,
} from "@roof-hawk/core";
import { useEffect, useState } from "react";

import { ErrorNote, GrowBox, Modal, Row } from "../../components/ui";
import { t, tn } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import {
  deleteRow,
  fetchEstimateChildren,
  fetchJobMultiOption,
  fetchJobPhotos,
  fetchScopeQualifiers,
  fetchProductVideos,
  fetchUpgradeProducts,
  insertRow,
  presentEstimate,
  signJobPhotos,
  updateRow,
  type CompanySettingsRow,
  fetchEstimateLineNames,
} from "../../lib/queries";
import { ComposeEmail } from "../email/ComposeEmail";
import { PresentedDocumentView } from "../present/PresentedDocumentView";
import { ProposalBlocks } from "./ProposalBlocks";
import { ProposalDocuments } from "./ProposalDocuments";
import { ProposalPhotos } from "./ProposalPhotos";

/**
 * Where the typed lines sit on the estimate.
 *
 * After the template's own copy, which starts at 1000, so the block reads last
 * on the package — the standing wording first, then what is particular to this
 * house.
 */
/*
 * Where the rep's own lines sit inside a package.
 *
 * Between the scope of work (1000) and the warranty (1010), which is where the
 * proposal we are copying puts them: what we are doing to this house, then what
 * we are doing about it in general. At 2000 they landed after everything,
 * below the wood bonus, which read as an afterthought.
 */
const HOME_ITEM_SORT_START = 1005;

/**
 * A `numeric` column as PostgREST hands it over, read back as a number.
 *
 * It arrives as a string so nothing is lost in transit, and `Number("")` is 0
 * — which would turn an unanswered rate into a 0% APR printed on a customer's
 * copy. Null stays null, and null means "use the company's programme".
 */
function numberOrNull(
  value: number | string | null | undefined,
): number | null {
  if (value == null || value === "") return null;
  const parsed = typeof value === "number" ? value : Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** The parts of an estimate that are only about the document. */
interface DocumentBlocks {
  name: string | null;
  hidden_sections: string[];
  section_order: string[];
  key_differentiator: string | null;
  /** What the rep changed in the chart column; null fills itself in. */
  compare_edits: ChartEdits | null;
}

const documentBlocks = (option: Estimate): DocumentBlocks => ({
  name: option.name ?? null,
  hidden_sections: [...(option.hidden_sections ?? [])],
  section_order: [...(option.section_order ?? [])],
  key_differentiator: option.key_differentiator ?? null,
  compare_edits: option.compare_edits ?? null,
});

/** What the rep has saved onto one line from this screen. */
type LinePatch = Partial<
  Pick<EstimateItem, "note" | "description" | "customer_visible">
>;

/** The rep's own block for the house, as the one box shows it. */
const homeText = (lines: readonly EstimateItem[]): string =>
  lines
    .filter((item) => isHomeSpecificSection(item.section))
    .map((item) => item.description ?? "")
    .join("\n");

const splitLines = (text: string): string[] =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

/**
 * The pages that read the same on every proposal, which the preview lets a
 * rep leave off this send (ADR 0294). Keyed by the document's own section id,
 * and named for a page that is not in the document to read a heading from.
 */
const FIXED_PAGES = [
  { id: "scope", label: "How Your Roof Goes On" },
  { id: "videos", label: "Walkthrough videos" },
  { id: "showcase", label: "Gutter guard slides" },
  { id: "work", label: "See Our Work" },
] as const;
type FixedPage = (typeof FIXED_PAGES)[number]["id"];

export function PresentEstimateModal({
  job,
  estimate,
  estimates,
  layouts,
  items,
  payments,
  totals,
  company,
  repName,
  repPhone,
  gmailAddress,
  gmailLoading,
  gmailError,
  senders,
  ownSender,
  onClose,
  onPresented,
}: {
  job: Job;
  estimate: Estimate;
  /** Every estimate on this job — the packages that could be offered. */
  estimates: readonly Estimate[];
  /** Read by job, not by estimate: the building is measured once (ADR 0030). */
  layouts: readonly RoofLayout[];
  items: readonly EstimateItem[];
  payments: readonly EstimatePayment[];
  totals: LadderTotals;
  company: CompanySettingsRow | null;
  repName: string | null;
  /**
   * The rep's own mobile. Prints on the cover in place of the company's,
   * and null falls back to it — see `buildPresentedDocument`.
   */
  repPhone?: string | null;
  /** The rep's connected Gmail, or null. Passed in, like everything else here. */
  gmailAddress?: string | null;
  /**
   * The mailboxes this person may send the estimate from.
   *
   * Jonathon, 2026-08-24: "when someone is sending an estimate they should have
   * the option to send it from someone else's email." Passed through to the
   * composer, which draws the picker; who may appear in it is decided by
   * `sendableAs` and enforced by the Edge Function.
   */
  senders?: readonly ConnectedSender[];
  ownSender?: ConnectedSender | null;
  /** True while Roof Board is still finding out which mailboxes are connected. */
  gmailLoading?: boolean;
  /** Why it could not find out, when it could not. */
  gmailError?: string | null;
  onClose: () => void;
  onPresented: () => void | Promise<void>;
}) {
  /*
   * Shingle type, colour, delivery instructions and existing damage were four
   * boxes on this screen, then four rows on the estimate's contract preview,
   * and are now nowhere at all — not on this screen, not on the contract, and
   * not in the database. Jonathon, 2026-08-24: "we don't need this. please
   * remove anything associated with it", and then "please remove it. this is
   * not a feature we have used or will use." See ADR 0056 and migration
   * 20260824160000.
   */
  const [recipientName, setRecipientName] = useState(
    job.contact?.full_name ?? "",
  );
  const [recipientEmail, setRecipientEmail] = useState(
    job.contact?.email ?? "",
  );
  const [recipientPhone, setRecipientPhone] = useState(
    job.contact?.cell ?? job.contact?.phone ?? "",
  );
  /*
   * Which estimates go in the packet, in the order the customer reads them.
   *
   * Ticked here, on the way out the door. Jonathon, 2026-08-19: "instead of a
   * recommend this option there needs to be a present option. that way it
   * doesn't present the things that aren't check." The builder's "Do not offer"
   * toggle still decides what is ticked to begin with — it is a standing
   * decision about an estimate — but the last word belongs to the person
   * looking at the customer, because which options to show is a fact about
   * this conversation and it changes on the doorstep.
   *
   * The estimate this screen was opened from is always in it. It is the one
   * being presented: the contract details, the home-specific lines and the
   * upgrades on this document all come from it, so a packet without it would be
   * a document about an estimate nobody chose to send.
   */
  const [alsoPresent, setAlsoPresent] = useState<string[]>(() =>
    estimatesToOffer(estimates, estimate)
      .map((option) => option.id)
      .filter((id) => id !== estimate.id),
  );
  const candidates = [...estimates].sort((a, b) => a.version - b.version);
  // What each option's first line is called, for the tick-list. The other
  // estimates' items are otherwise only ever read at send; a row reading
  // "Option 2" in front of a customer says nothing about which one it is.
  const lineNames = useAsync(
    () => fetchEstimateLineNames(candidates.map((e) => e.id)),
    [candidates.map((e) => e.id).join(",")],
  );
  // Made distinct, so two options that start with the same line are not two
  // identical rows. This estimate's lines are the ones in hand, not the fetch.
  const optionLabels = estimateOptionLabels(
    candidates,
    new Map<string, readonly PackageCopyLine[]>(lineNames.data ?? []).set(
      estimate.id,
      items,
    ),
  );
  /** One option priced from its own rows, the way the packet prices it. */
  const ladderFor = (
    option: Estimate,
    children: Awaited<ReturnType<typeof fetchEstimateChildren>>,
  ) =>
    computeLadder({
      estimate: option,
      items: children.items,
      changeOrders: children.changeOrders,
      materialOrders: children.materialOrders,
      workOrders: children.workOrders,
      expenses: children.expenses,
      returns: children.returns,
      jobType: job.job_type,
      place: job,
      layouts: [...layouts],
      workOrderCrews: children.workOrders.map((order) => order.crew ?? null),
    });
  /*
   * The order the customer reads the options in.
   *
   * Jonathon, 2026-10-20: "it should typically start with most expensive and
   * work it's way down" — and be sortable. So the list below is in price order
   * until the rep presses an arrow, and then it is in theirs, for this send
   * (ADR 0294). Each option is priced here only to be put in order; the
   * document prices them again as it is built, and sorts by that.
   */
  const [chosenOrder, setChosenOrder] = useState<string[]>([]);
  const optionTotals = useAsync(async () => {
    const out = new Map<string, Cents | null>();
    if (candidates.length < 2) return out;
    await Promise.all(
      candidates.map(async (option) => {
        if (option.id === estimate.id) {
          out.set(option.id, totals.grandTotal);
          return;
        }
        try {
          const children = await fetchEstimateChildren(option.id);
          out.set(option.id, ladderFor(option, children).grandTotal);
        } catch {
          out.set(option.id, null);
        }
      }),
    );
    return out;
  }, [candidates.map((e) => e.id).join(","), totals.grandTotal]);
  const ordered = packetOrder(
    candidates.map((option) => ({
      option,
      id: option.id,
      total: optionTotals.data?.get(option.id) ?? null,
    })),
    chosenOrder,
  ).map((entry) => entry.option);
  /** Move one option up or down the list, which writes the whole order. */
  const moveOption = (at: number, by: -1 | 1) => {
    const to = at + by;
    if (to < 0 || to >= ordered.length) return;
    const ids = ordered.map((option) => option.id);
    const [moved] = ids.splice(at, 1);
    if (moved === undefined) return;
    ids.splice(to, 0, moved);
    setChosenOrder(ids);
  };
  const offeredEstimates = ordered.filter(
    (option) => option.id === estimate.id || alsoPresent.includes(option.id),
  );
  /*
   * Upgrades the rep has taken off this proposal.
   *
   * Jonathon, 2026-08-15: "upgrades also need to be able to delete or toggle
   * off from presentation." Per send rather than per product — a customer with
   * no gutters to speak of should not be offered gutter guards, and that is not
   * a reason to stop offering them to the next person.
   */
  const [droppedUpgrades, setDroppedUpgrades] = useState<string[]>([]);
  /*
   * The quantity the rep typed over an upgrade's measured one, by name.
   *
   * Jonathon, 2026-09-30: "we need to be able to edit the quantities on the
   * upgrades on the pop up on the estimate builder." Per send, like the tick
   * beside it: the measurement on the job is not changed, only what this
   * proposal charges. Held as typed; `priceUpgrade` ignores anything that is
   * not a count above zero and keeps the measured quantity.
   */
  const [upgradeQty, setUpgradeQty] = useState<Record<string, string>>({});
  const typedUpgradeQty = (name: string): number | null => {
    // "1,000" is a thousand, not a typo to ignore.
    const typed = (upgradeQty[name] ?? "").replace(/,/g, "").trim();
    return typed === "" ? null : Number(typed);
  };
  /** Typed, but not a count above zero — so the measured one is priced. */
  const upgradeQtyIgnored = (name: string): boolean => {
    const typed = typedUpgradeQty(name);
    return typed !== null && !(Number.isFinite(typed) && typed > 0);
  };
  /*
   * Whether the financing page goes out with this one.
   *
   * Jonathon, 2026-08-21: it has to be possible to turn off — two pages of
   * lending terms under a $600 repair sell nothing. The switch already
   * existed: `estimates.show_financing` is three-state, set on the estimate
   * card and deferring to `company_settings.show_financing_default`, and the
   * card has said "Hide on estimate" since the column was added. Nothing ever
   * read it here, so the page went out regardless.
   *
   * So this starts as whatever that setting says and can still be flipped for
   * this send. It is not written back: the estimate's setting is a standing
   * decision, and a rep dropping the page at one doorstep is not making one.
   */
  const [financingPage, setFinancingPage] = useState(() =>
    showFinancing(
      estimate.show_financing,
      company?.show_financing_default,
      job.job_type,
      company?.financing_offered,
    ),
  );
  /*
   * Whether the chart comparing the roofs goes out with this one.
   *
   * Jonathon, 2026-10-07: "i do want a rep to be able to hide it when they
   * want to" — and only for this send, like the financing page: it starts on
   * every time this screen opens and is not written back (ADR 0274).
   */
  const [comparePage, setComparePage] = useState(true);
  /*
   * The colour cards the rep has switched off, by `ColorCard.key`.
   *
   * Jonathon, 2026-09-18: "we need to be able to toggle on and off color
   * options. sometimes it's not relevant on repairs" — and then, per card
   * rather than per page: "we can keep roof replacements on but gutters off".
   * The page is built from whatever the document offers, so a repair that
   * names a shingle carries its chart whether or not a colour is being chosen,
   * and on a repair the answer is usually "match what is there".
   *
   * Unlike the financing page this IS written back, to
   * `estimates.hidden_colors`: whether a colour is being chosen is a fact about
   * the work, not about one doorstep, and the rep should not have to remember
   * it at the next send. A card switched off is absent from the frozen
   * document; with every card off, so is the page.
   */
  const [hiddenColors, setHiddenColors] = useState<string[]>(() => [
    ...(estimate.hidden_colors ?? []),
  ]);
  const upgradeProducts = useAsync(fetchUpgradeProducts, []);
  /*
   * The protective clauses, so the document can print the ones its lines call
   * for (ADR 0100). Resolved here and not stored on the line, so a rewording
   * in Tools reaches every proposal that has not been sent yet — and a
   * proposal already sent keeps the copy it froze at send (ADR 0032).
   *
   * A failed read leaves the list empty and no clause in any line's scope.
   * That is a proposal missing its small print, which is worth knowing about,
   * so it is not silently swallowed — `useAsync` surfaces the error the way it
   * does for the upgrades beside it.
   */
  const scopeQualifiers = useAsync(fetchScopeQualifiers, []);
  /*
   * The multi-option box as the database has it, for the line under the list
   * of estimates and the warning beside it. The `job` this was handed can be
   * behind (ADR 0120); until the read lands it is what there is. The document
   * itself reads the box again as it is built, and the preview warns off what
   * was built — that is the answer that goes out.
   */
  const flags = useAsync(() => fetchJobMultiOption(job.id), [job.id]);
  const multiOptionNow = flags.data?.multi_option ?? Boolean(job.multi_option);
  const multiOptionManualNow =
    flags.data?.multi_option_manual ?? Boolean(job.multi_option_manual);
  /*
   * What each ticked estimate is, as far as this screen can see: its trade,
   * and the lines of the one on screen. The other options' lines are loaded
   * only when the document is built, so a trade-less roof among them is caught
   * by the preview rather than here.
   */
  const offeredRoofOptions = offeredEstimates.map((option) => ({
    trade: option.trade ?? null,
    itemTrades:
      option.id === estimate.id ? items.map((item) => item.trade) : undefined,
  }));
  /*
   * The upgrades this packet may offer at all.
   *
   * Jonathon, 2026-08-27: the GAF Master Flow pipe boots are excluded from
   * metal roof estimates — they are a shingle product, and on a ribbed panel
   * they seat on nothing. Off the list rather than unticked, because unticked
   * is a thing a rep can tick.
   *
   * Read off every option being offered: a packet quoting a shingle roof
   * beside a standing seam one still offers them, since the upgrades sit in
   * one list under all the packages. See `upgradeSuitsRoofs`.
   */
  const offerableUpgrades = (upgradeProducts.data ?? []).filter((product) =>
    upgradeSuitsRoofs(
      product,
      offeredEstimates.map((option) => option.roof_type),
    ),
  );
  /*
   * The colour cards this document would carry, for the switches below.
   *
   * The same question `buildPresentedDocument` answers when the document is
   * built, asked early so the switches are on screen before Present is
   * pressed. A card is found by the roof type and the line names of each
   * offered option, and the other options' lines are not loaded on this
   * screen, so they are fetched — names only, nothing priced. Gutters are a
   * card when anything offered puts gutters on the house, an upgrade included.
   */
  const offeredIds = offeredEstimates.map((option) => option.id).join(",");
  const colorCardsOffered = useAsync<ColorCard[]>(async () => {
    const packages = await Promise.all(
      offeredEstimates.map(async (option) => {
        const lines =
          option.id === estimate.id
            ? items
            : (await fetchEstimateChildren(option.id)).items;
        return {
          name: option.name ?? "Estimate",
          roofType: option.roof_type ?? null,
          lineNames: lines
            .filter(
              (item) =>
                item.work_doing !== false && item.customer_visible !== false,
            )
            .map((item) => (item.description ?? "").trim())
            .filter((name) => name !== ""),
        };
      }),
    );
    const offeredNames = [
      ...packages.flatMap((pkg) => pkg.lineNames),
      ...(upgradeProducts.data ?? []).map((product) => product.name),
    ];
    return colorCards(packages, offeredNames);
  }, [offeredIds, upgradeProducts.data]);
  /*
   * Whether this document would carry the comparison chart, for its switch.
   *
   * Asked of core with every offered option's line trades, as the document is
   * built — not `offeredRoofOptions`, which has only this estimate's — so a
   * trade-less roof among the others is charted and switchable alike, and two
   * roofs set for different buildings get neither (ADR 0274).
   *
   * Each answer carries what it was asked of, and only the answer for what is
   * on screen now counts: while the box is read fresh, the last answer is for
   * a different packet, and a switch shown off it would promise a chart the
   * document leaves out.
   *
   * When the other options' lines cannot be read, the answer is unknown
   * (`null`), not "no": the build at send reads them again and may still chart
   * the roofs, so with two or more options offered the switch is shown anyway.
   * A rep must always be able to leave out a chart that goes out.
   */
  const compareAsked = [
    offeredIds,
    items.map((item) => item.trade ?? "").join(","),
    multiOptionNow,
    multiOptionManualNow,
  ].join("|");
  const comparesRoofs = useAsync(async () => {
    try {
      const options = await Promise.all(
        offeredEstimates.map(async (option) => ({
          trade: option.trade ?? null,
          itemTrades: (option.id === estimate.id
            ? items
            : (await fetchEstimateChildren(option.id)).items
          ).map((item) => item.trade),
        })),
      );
      return {
        asked: compareAsked,
        compares: documentComparesRoofs(
          multiOptionNow,
          multiOptionManualNow,
          options,
        ) as boolean | null,
      };
    } catch {
      return { asked: compareAsked, compares: null };
    }
  }, [compareAsked]);
  const showCompareSwitch =
    comparesRoofs.data?.asked === compareAsked &&
    (comparesRoofs.data.compares ?? offeredEstimates.length >= 2);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [revision, setRevision] = useState(1);
  const [emailing, setEmailing] = useState(false);
  /*
   * The document the rep is looking at, or null when they are not.
   *
   * Held here rather than fetched back from anywhere, because there is nothing
   * to fetch: a preview is never written down.
   */
  const [previewDoc, setPreviewDoc] = useState<PresentedDocument | null>(null);
  /**
   * The packages whose Key Differentiator that document prints, which is
   * where the preview offers the box to edit it. Set with the document, from
   * the same packages, so the two cannot disagree.
   */
  const [keyLineIds, setKeyLineIds] = useState<string[]>([]);
  /**
   * Every offered package's rows as the preview last fetched them, so the
   * other packages' words can be edited here as well as this one's. This
   * estimate's own rows are the ones handed in.
   */
  const [packageItems, setPackageItems] = useState<Map<string, EstimateItem[]>>(
    () => new Map(),
  );
  /**
   * Which package's words the preview is showing to edit.
   *
   * Jonathon, 2026-10-20: "i need to able to remove the what you get from any
   * of the packages when reviewing what's presented." It used to be this
   * estimate's only; the others had to be opened one at a time (ADR 0294).
   */
  const [editingId, setEditingId] = useState(estimate.id);
  /** The fixed pages left off this send. Not remembered, like financing. */
  const [pagesOff, setPagesOff] = useState<FixedPage[]>([]);
  /**
   * Square counts on the customer's copy, and whether the rep has said to send
   * it anyway.
   *
   * The approval belongs to one preview, not to the screen: going back to edit
   * clears it, so a rep who fixes one leak and introduces another is asked
   * again rather than sending on a decision they made about a different
   * document.
   */
  const [leaks, setLeaks] = useState<SquareCountLeak[]>([]);
  const [sendAnyway, setSendAnyway] = useState(false);
  /*
   * The lines this particular house needs, one per row, by estimate.
   *
   * Everything else in a package is the same wording every time — it comes off
   * the template. This is the block that makes the proposal about the house the
   * rep is standing in front of, so it is typed here, on the preview, where
   * they can see where it lands. A package not yet typed into reads its own
   * rows; `homeWritten` is what this screen has put on an estimate since.
   */
  const [homeDraft, setHomeDraft] = useState<Record<string, string>>({});
  const [homeWritten, setHomeWritten] = useState<Record<string, string>>({});

  /*
   * The parts of each estimate that are only about the document: its name,
   * which blocks the customer reads, in what order, and the line under the
   * package name. They are edited here, so they are held here as well: the
   * `estimates` are fetched by the card behind this modal and do not come
   * back until it closes, and a preview built from a stale prop would show the
   * rep the opposite of what they just pressed.
   */
  const [blocksById, setBlocksById] = useState<Record<string, DocumentBlocks>>(
    () =>
      Object.fromEntries(
        [...estimates, estimate].map((option) => [
          option.id,
          documentBlocks(option),
        ]),
      ),
  );
  const [keyDraft, setKeyDraft] = useState<Record<string, string>>({});
  /**
   * The comparison chart's changes as the rep is typing them, by estimate,
   * before they are saved on leaving the box. Absent means the column reads
   * as saved.
   */
  const [editDrafts, setEditDrafts] = useState<Record<string, ChartEdits>>({});
  const [nameDraft, setNameDraft] = useState<Record<string, string>>({});

  /** An estimate as the document should read it, prop plus what was pressed. */
  const asPresented = <T extends { id: string }>(option: T): T =>
    blocksById[option.id]
      ? ({ ...option, ...blocksById[option.id] } as T)
      : option;

  /*
   * The words on every line, as saved from this screen.
   *
   * Laid over the rows the document is built from, by line id, so a preview
   * rebuilt after an edit reads the edit — the rows this estimate was opened
   * with are the card's, and do not come back until the modal closes.
   */
  const [lineEdits, setLineEdits] = useState<Record<string, LinePatch>>({});
  /** What is being typed into a box, by `<line id>:<field>`, until it is left. */
  const [lineDrafts, setLineDrafts] = useState<Record<string, string>>({});

  /** One estimate's rows, with everything saved on this screen laid over them. */
  const withEdits = (
    estimateId: string,
    lines: readonly EstimateItem[],
  ): EstimateItem[] => {
    const edited = lines.map((line) =>
      line.id && lineEdits[line.id] ? { ...line, ...lineEdits[line.id] } : line,
    );
    const typed = homeWritten[estimateId];
    if (typed === undefined) return edited;
    return [
      ...edited.filter((line) => !isHomeSpecificSection(line.section)),
      ...splitLines(typed).map(
        (description, at) =>
          ({
            id: `typed-${estimateId}-${at}`,
            description,
            section: HOME_SPECIFIC_SECTION,
            work_doing: true,
            customer_visible: true,
            sort_order: HOME_ITEM_SORT_START + at * 10,
          }) as EstimateItem,
      ),
    ];
  };

  /** The rows an estimate's editors are drawn from: this one's, or as fetched. */
  const rowsOf = (estimateId: string): EstimateItem[] =>
    estimateId === estimate.id
      ? [...items]
      : (packageItems.get(estimateId) ?? []);

  /*
   * Build the preview again once whatever was just saved has landed in state.
   *
   * A save that called `preview` itself would call it from the render it was
   * pressed in, and that render still holds the words from before the save.
   */
  const [rebuild, setRebuild] = useState(0);
  const requestRebuild = () => setRebuild((n) => n + 1);
  useEffect(() => {
    if (rebuild > 0 && previewDoc) void preview();
    // Only a save asks for this; `preview` is a new function every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rebuild]);

  /**
   * Save a switch, an arrow, the name or the key differentiator.
   *
   * Straight onto that estimate's row and into the state above, then the
   * preview is rebuilt if one is open, because what changed is what it prints.
   * Says whether the write landed, so a box keeps what was typed when it did
   * not and the rep can try again.
   */
  const saveBlocks = async (
    estimateId: string,
    patch: Partial<Estimate>,
  ): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await updateRow("estimates", estimateId, patch);
      setBlocksById((prior) => ({
        ...prior,
        [estimateId]: {
          ...(prior[estimateId] ??
            documentBlocks(
              estimates.find((option) => option.id === estimateId) ?? estimate,
            )),
          ...(patch as object),
        },
      }));
      requestRebuild();
      return true;
    } catch (caught) {
      setError((caught as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  /** One colour card's switch: onto the estimate row, and into the preview. */
  const toggleColorCard = async (key: string) => {
    const next = hiddenColors.includes(key)
      ? hiddenColors.filter((entry) => entry !== key)
      : [...hiddenColors, key];
    setHiddenColors(next);
    setBusy(true);
    setError(null);
    try {
      await updateRow("estimates", estimate.id, { hidden_colors: next });
      if (previewDoc) await preview({ hiddenColors: next });
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Save one line's words, or its switch, onto its row.
   *
   * The same columns the builder writes, so a scope changed here is the scope
   * everywhere — there is one copy of it, not one for the proposal. Says
   * whether the write landed, as `saveBlocks` does.
   */
  const saveLine = async (
    lineId: string,
    patch: LinePatch,
  ): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await updateRow("estimate_items", lineId, patch);
      setLineEdits((prior) => ({
        ...prior,
        [lineId]: { ...prior[lineId], ...patch },
      }));
      requestRebuild();
      return true;
    } catch (caught) {
      setError((caught as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  /**
   * Save a box once the rep leaves it, if they changed anything.
   *
   * What was typed stays in the box until the save lands: a failed write
   * leaves it there, beside the error, for the rep to try again.
   */
  const leaveLineBox = (line: EstimateItem, field: "note" | "description") => {
    const draftKey = `${line.id}:${field}`;
    const typed = lineDrafts[draftKey];
    const dropDraft = () =>
      setLineDrafts(({ [draftKey]: _left, ...rest }) => rest);
    if (typed === undefined) return;
    if (typed === (line[field] ?? "")) return dropDraft();
    if (field === "description") {
      // A line with no name is not printed at all, so emptying the name box
      // would take the line off by the back door. Its words can be emptied,
      // and a wording block has its own switch.
      if (typed.trim() === "") return dropDraft();
      void saveLine(line.id, { description: typed.trim() }).then(
        (saved) => saved && dropDraft(),
      );
      return;
    }
    void saveLine(line.id, { note: typed.trim() || null }).then(
      (saved) => saved && dropDraft(),
    );
  };

  const companyFacts: CompanyFacts = {
    name: company?.name,
    address: company?.address,
    email: company?.email,
    phone: company?.phone,
    certifications: company?.certifications,
    reviews: company?.reviews,
    insurance: company?.insurance,
    financingHeading: company?.financing_heading,
    financingApplicationUrl: company?.financing_application_url,
    workGalleryUrl: company?.work_gallery_url,
    // The lending programmes the calculator offers, straight off the row.
    // Validated in core, which also holds the fallback list (ADR 0202).
    financingPrograms: company?.financing_programs,
  };

  /*
   * The contract's merge fields, as they stand right now.
   *
   * Computed on every render rather than inside `present`, because the gaps in
   * it are shown on the screen while the rep is filling it in — a warning that
   * only arrives when the button is pressed is a warning that arrives too late
   * to be useful and too late to be ignored gracefully.
   */
  const contractValues: ContractValues = {
    "company.name": company?.name,
    "company.address": company?.address,
    "company.email": company?.email,
    // The cancellation notice tells the customer to email this address to
    // exercise their 3-day right of rescission, so it had better be one
    // somebody reads. Its own field for that reason; falls back to the general
    // address in core when it is not set.
    "company.cancellation_email": company?.cancellation_email,
    // The insurance paragraphs give this out three times — to the adjuster, to
    // billing and to the insurance department.
    "company.phone": company?.phone,
    "customer.name": job.contact?.full_name,
    "job.address": job.address,
  };

  /*
   * Blanks warn; they never block.
   *
   * They used to throw, which meant a rep standing in a driveway with a
   * customer could not present an estimate because nobody had typed a shingle
   * colour. Jonathon, 2026-08-13: "this should not prevent me from presenting
   * an estimate ever."
   *
   * A blank is still worth saying out loud — `fill()` leaves the token visible
   * on the contract on purpose, so an unfilled one prints as
   * "{{company.phone}}" in front of a customer. So it is said here, in the
   * modal, while there is still something to do about it, and the rep decides.
   */
  const gaps = unfilledContractTokens(contractValues);
  const ourGaps = gaps.filter((token) => token.startsWith("company."));
  const theirGaps = gaps.filter((token) => !token.startsWith("company."));

  /**
   * Every offered estimate, priced and dressed as a package.
   *
   * Each one is fetched and run through `computeLadder` here rather than
   * trusting anything the card happens to be holding: only the selected
   * estimate's children are loaded on screen, and a packet quotes four
   * estimates nobody has opened. A package priced off stale state is a wrong
   * number on a contract.
   *
   * Returns an empty list for a single estimate — that is not a choice, and the
   * document is built the way it always was.
   */
  const buildPackages = async (): Promise<{
    packages: PackageInput[];
    /**
     * Every trade across every package, for `contractKindFor`. Collected here
     * because this is the only place the other estimates' line items are ever
     * loaded — the card on screen holds one estimate's.
     */
    trades: (string | null)[];
    /** Every product across every package, for the walkthroughs. Same reason. */
    productIds: string[];
    /** Each package's rows as fetched, for the editors on the preview. */
    items: Map<string, EstimateItem[]>;
    /**
     * The same estimates in the order that decides which bought one is the
     * main estimate: version order, whatever order they are read in
     * (`mainEstimateOrder`, ADR 0294).
     */
    leadOrder: string[];
  }> => {
    if (offeredEstimates.length < 2)
      return {
        packages: [],
        trades: [],
        productIds: [],
        items: new Map(),
        leadOrder: [],
      };

    const built = await Promise.all(
      offeredEstimates.map(async (option) => {
        const children = await fetchEstimateChildren(option.id);
        const ladder = ladderFor(option, children);
        // The rows as the customer will read them: what was fetched, with
        // whatever was saved on the preview since laid over it.
        const lines = withEdits(option.id, children.items);

        /*
         * What this one estimate sells, so a package with no trade on it is
         * not called a new roof. The whole document's kind is settled later,
         * from every trade in the packet; this is the same question asked of
         * one option, which is what its own heading should follow.
         */
        const optionTrades = [
          option.trade,
          ...children.items.map((item) => item.trade),
        ];
        const optionKind = contractKindFor({
          bidType: job.bid_type ?? null,
          trades: optionTrades,
        });

        return {
          pkg: {
            estimateId: option.id,
            // The option's own line names decide what it is called when
            // nobody has typed a package name (Jonathon, 2026-09-15).
            name: packageNameFor(asPresented(option), optionKind, lines),
            // The line under the package name is the template's Key
            // Differentiator, never the warranty tier — printing that is what
            // put "silver_pledge" on a customer's page (Jonathon, 2026-08-19).
            //
            // Passed whatever this option is. Whether it prints depends on the
            // other options as well, and core decides that from `trade`, then
            // `itemTrades` when the trade is blank — see
            // `keyDifferentiatorApplies`, which is also what the box on the
            // preview is drawn from.
            headline: asPresented(option).key_differentiator?.trim() || null,
            // What the rep changed in the chart column (ADR 0318).
            compareEdits: asPresented(option).compare_edits ?? null,
            kind: optionKind,
            total: ladder.grandTotal,
            payments: children.payments,
            plan: planTermsOf(asPresented(option)),
            // The rep's own block is a replacement's — a repair quote is
            // already nothing but items specific to this house.
            blocks: packageBlocks(lines, {
              homeSpecific: optionKind === "replacement",
              // Each option answers for itself. Two packages on one job can
              // want different paragraphs — the cheap one may not carry the
              // timeframe guarantee the expensive one is sold on.
              hidden: asPresented(option).hidden_sections,
              order: asPresented(option).section_order,
              jobType: job.job_type,
              qualifiers: scopeQualifiers.data ?? [],
            }),
            roofType: option.roof_type ?? null,
            trade: option.trade ?? null,
            // Its lines' trades, which say whether a trade-less estimate is a
            // roof (`isWholeRoofOption`): a rival of the other roofs and a
            // carrier of the Key Differentiator. The same lines `optionTrades`
            // reads for the agreement.
            itemTrades: children.items.map((item) => item.trade),
          },
          trades: optionTrades,
          productIds: children.items.flatMap((item) =>
            item.product_id ? [item.product_id] : [],
          ),
          items: children.items,
          version: option.version,
        };
      }),
    );

    // Most expensive first, by the prices just worked out, unless the rep put
    // them in an order of their own (ADR 0294).
    const sorted = packetOrder(
      built.map((entry) => ({
        ...entry,
        id: entry.pkg.estimateId,
        total: entry.pkg.total,
      })),
      chosenOrder,
    );
    // Two options that start with the same line would otherwise reach the
    // customer as two identical headings and two identical tabs (review of
    // #254). Numbered in version order whatever order they are read in, so
    // "(2)" is the later one, as it is on our own screens (ADR 0147).
    const byVersion = [...sorted].sort((a, b) => a.version - b.version);
    const names = distinguishNames(byVersion.map((entry) => entry.pkg.name));
    const nameOf = new Map(
      byVersion.map((entry, at) => [entry.id, names[at] ?? entry.pkg.name]),
    );
    return {
      packages: sorted.map((entry) => ({
        ...entry.pkg,
        name: nameOf.get(entry.id) ?? entry.pkg.name,
      })),
      trades: sorted.flatMap((entry) => entry.trades),
      productIds: sorted.flatMap((entry) => entry.productIds),
      items: new Map(sorted.map((entry) => [entry.id, entry.items])),
      leadOrder: mainEstimateOrder(sorted),
    };
  };

  /**
   * The upgrades to offer, priced for this roof.
   *
   * Priced through `priceUpgrade` in packages/core, which runs the same
   * arithmetic that fills the quantity box when a rep adds one of these to an
   * order by hand — so unless the rep types over it, the gutters on the
   * proposal and the gutters on the work order that follows are the same
   * number of feet. A quantity typed here wins, for this send only.
   *
   * An upgrade the roof cannot price is dropped rather than offered at nothing:
   * a job with no eave measured has no gutter footage, and "Gutters — $0.00" on
   * a customer's quote is a promise nobody meant to make.
   */
  const buildUpgrades = (): UpgradeInput[] =>
    offerableUpgrades
      .filter((product) => !droppedUpgrades.includes(product.name))
      .flatMap((product) => {
        const priced = priceUpgrade(
          product,
          layouts,
          typedUpgradeQty(product.name),
        );
        if (!priced) return [];
        return [
          {
            key: product.name,
            name: product.name,
            blurb: product.description,
            price: priced.price,
            // Which packages already cover it. Read off each estimate rather
            // than worked out from its line items — Bulletproof's high temp is
            // inside a lump sum and has no line to find.
            includedIn: offeredEstimates
              .filter((option) =>
                (option.included_upgrades ?? []).includes(product.name),
              )
              .map((option) => option.id),
          },
        ];
      });

  /**
   * The document, exactly as the customer would get it. Writes nothing.
   *
   * One builder behind both buttons: a preview that took a different path to
   * the same screen would be a preview of something else.
   */
  const buildDocument = async (
    /**
     * What was just pressed, ahead of the state it will land in. A switch that
     * rebuilt the preview from the render it was pressed in would show the
     * page it had just turned off.
     */
    over: { hiddenColors?: readonly string[] } = {},
  ) => {
    {
      const today = new Date();
      const {
        packages,
        trades: packageTrades,
        productIds: packageProductIds,
        items: fetchedItems,
        leadOrder,
      } = await buildPackages();
      const ownLines = withEdits(estimate.id, items);
      const own = asPresented(estimate);

      /*
       * What this document sells, for `contractKindFor`.
       *
       * A packet's trades come from every package in it; a single-estimate
       * document's from the one on screen. One replacement anywhere puts the
       * replacement contract on the bottom, because a packet is one contract
       * however many prices it quotes.
       */
      const trades =
        packages.length > 0
          ? packageTrades
          : [estimate.trade, ...items.map((item) => item.trade)];

      /*
       * Read fresh, not from what the section below happens to be holding.
       *
       * A rep uploads a photograph and presses Present in the same breath. The
       * snapshot is frozen the moment it is written and cannot be corrected
       * afterwards, so it asks the database what the photographs are rather
       * than trusting a component's state to have caught up.
       */
      const photos = await fetchJobPhotos(job.id);
      /*
       * The multi-option box, for the same reason and more so. The database
       * changes it whenever an estimate on this job is added, deleted or
       * retyped (ADR 0120), so `job` can say what it said before the rep
       * started a second estimate — and the document freezes whatever it is
       * handed, for good.
       */
      // And whether a person set it, read in the same breath: set by hand, it
      // lets the customer buy two roofs together (ADR 0124), and the rep may
      // have ticked it seconds ago on the card.
      const {
        multi_option: multiOption,
        multi_option_manual: multiOptionManual,
      } = await fetchJobMultiOption(job.id);

      const kind = contractKindFor({ bidType: job.bid_type ?? null, trades });
      /*
       * The walkthroughs, for whatever this document actually sells.
       *
       * Across every package, not only the estimate on screen — a packet
       * offering a repair as one of three options should still show how the
       * repair is done. `buildPresentedDocument` drops the duplicates when the
       * same repair appears in two of them.
       *
       * A failure here is not allowed to stop a rep presenting. The videos are
       * worth having and nothing on the page depends on them, so an unreachable
       * catalogue costs the section and not the proposal.
       */
      const productIds = new Set(
        [...items.map((item) => item.product_id), ...packageProductIds].filter(
          (id): id is string => Boolean(id),
        ),
      );
      const productVideos = await fetchProductVideos()
        .then((videos) => videos.filter((v) => productIds.has(v.productId)))
        .catch(() => []);

      const onePackage: PackageInput = {
        estimateId: estimate.id,
        name: packageNameFor(own, kind, ownLines),
        // The line under the package name is the template's Key
        // Differentiator, never the warranty tier — printing that is what
        // put "silver_pledge" on a customer's page (Jonathon, 2026-08-19).
        //
        // Passed, and left off by core: this path IS the single quote —
        // `buildPackages` returns nothing below two options — and a single
        // quote never carries it (Jonathon, 2026-09-08; see
        // `keyDifferentiatorApplies`). Passed anyway so the rule lives in one
        // place rather than being restated here.
        headline: own.key_differentiator?.trim() || null,
        compareEdits: own.compare_edits ?? null,
        kind,
        total: totals.grandTotal,
        payments,
        plan: planTermsOf(estimate),
        blocks: packageBlocks(ownLines, {
          homeSpecific: kind === "replacement",
          hidden: own.hidden_sections,
          order: own.section_order,
          jobType: job.job_type,
          qualifiers: scopeQualifiers.data ?? [],
        }),
        roofType: estimate.roof_type ?? null,
        trade: estimate.trade ?? null,
        itemTrades: items.map((item) => item.trade),
      };

      /*
       * The financing terms, from the estimate this screen was opened from.
       *
       * A packet may offer four options, but it is ONE agreement with one
       * customer and one lender — the rate, the term and the down payment
       * belong to the deal, not to whichever option they pick. So they come
       * from the estimate the rep is presenting, with the company's own
       * programme filling in whatever it left null. Nothing typed anywhere
       * leaves every figure the illustration it has always been (ADR 0201).
       */
      const financing = {
        downPayment:
          estimate.financing_down_payment == null
            ? null
            : toCents(estimate.financing_down_payment),
        termMonths:
          estimate.financing_term_months ??
          company?.financing_term_months ??
          null,
        apr:
          estimate.financing_apr == null
            ? (numberOrNull(company?.financing_apr) ?? null)
            : numberOrNull(estimate.financing_apr),
      };
      // A term with no rate, or a rate with no term, goes nowhere: Jonathon,
      // 2026-10-01, "Don't let a rep type a term with no rate and visa versa"
      // (ADR 0253). Refused before anything is built, so nothing can be sent.
      // A half-filled default in Tools is named as the company's, and only
      // refused when the financing page carries it to the customer (#644).
      const gap = presentFinancingGap(
        {
          termMonths: estimate.financing_term_months,
          apr: numberOrNull(estimate.financing_apr),
        },
        {
          termMonths: company?.financing_term_months,
          apr: numberOrNull(company?.financing_apr),
        },
        financingPage,
      );
      if (gap) throw new Error(gap);

      const document = buildPresentedDocument({
        date: today.toISOString().slice(0, 10),
        customerName: job.contact?.full_name ?? null,
        customerEmail: job.contact?.email ?? null,
        // The cell, falling back to the other number they gave us — the same
        // rule the rest of the app reads a contact's phone by. It prints on the
        // cover and fills the signing form in, so "we only have their house
        // number" should not mean an empty box (Jonathon, 2026-08-19).
        customerPhone: primaryPhone(job.contact) ?? null,
        propertyAddress: job.address ?? null,
        estimateNumber: String(job.job_number),
        // The rep signs the agreement and their number goes on the cover.
        // Neither is asked for here: they are the job's rep, and a job
        // without one falls back to the company in core.
        repName,
        repPhone: repPhone ?? null,
        // The storage path, not a link: a presented document is frozen and a
        // signed URL is not. The Edge Function that serves the customer's copy
        // signs each one at the moment it is asked for. See the 20260813150000
        // migration.
        inspectionPhotos: photos.map((photo) => ({
          url: photo.path,
          caption: photo.caption,
        })),
        // A public URL, unlike the photographs above: ours, identical for every
        // customer, and it must still play when the proposal is opened next
        // week. See the 20260905110000 migration.
        productVideos: productVideos.map(
          ({ url, posterUrl, title, caption }) => ({
            url,
            posterUrl,
            title,
            caption,
          }),
        ),
        // Two or more and the customer chooses; one and it is the price. Either
        // way the document is a list of packages.
        packages: packages.length > 0 ? packages : [onePackage],
        // Which bought option is the main one — the upgrades land on it and its
        // payment schedule is used. Version order, kept apart from the reading
        // order above, so moving an option up the page moves no money
        // (ADR 0294).
        leadOrder,
        // Whether choosing one un-chooses the last. The rep ticks "Customer can
        // buy more than one of these" on the estimate card, and until
        // 2026-09-09 nothing carried that as far as the customer: they were
        // offered two things to buy and could sign for one (ADR 0120). Frozen
        // into the document here, so a link that has gone out keeps the rule it
        // went out under — read fresh above, never off `job`.
        multiOption,
        multiOptionManual,
        upgrades: buildUpgrades(),
        company: companyFacts,
        contract: contractValues,
        bidType: job.bid_type ?? null,
        trades,
        // What the document is allowed to call the building. A commercial job
        // reads "property owners" and "your property" throughout, not just in
        // the one heading that already switched (Jonathon, 2026-08-27).
        jobType: job.job_type ?? null,
        show: {
          financing: financingPage,
          compare: comparePage,
          // The pages that read the same every time, which the preview lets
          // a rep leave off this send (ADR 0294).
          scope: !pagesOff.includes("scope"),
          productVideos: !pagesOff.includes("videos"),
          showcase: !pagesOff.includes("showcase"),
          work: !pagesOff.includes("work"),
        },
        financing,
        // Which colour cards to leave out. Read fresh off what was just
        // pressed when a switch rebuilds the preview — see `over`.
        hiddenColors: over.hiddenColors ?? hiddenColors,
      });

      /*
       * A square count on a customer's copy: a warning, not a refusal.
       *
       * It refused until 2026-09-08, on the reasoning that a square count is
       * not something the rep chose to leave out and there is no reading of
       * "present it anyway" that is right. Jonathon overruled that from a
       * doorstep — Monica Marsh's 1.69-square repair could not be handed over
       * at all: "i don't want it to block us but i do want it to warn us. if we
       * approve it we should be able to move forward."
       *
       * He is right about the two mistakes. A square count on a proposal
       * invites one awkward question; a rep standing in front of a customer
       * with nothing to hand them is a job lost. So the guard is loud, it says
       * exactly what it found and where, and the rep decides.
       */
      const leaks = squareCountLeaks(document);

      // A payment schedule that does not add up to the contract. ADR 0031: a
      // schedule with an error must not be saved or presented. Checked for
      // every package, not just the one on screen — a packet can carry four
      // schedules nobody has looked at, and one broken one is a wrong set of
      // numbers on a signed contract.
      const quoted = packages.length > 0 ? packages : [onePackage];
      const schedules = quoted.map((pack) => ({
        name: quoted.length > 1 ? pack.name : null,
        schedule: paymentSchedule(pack.total, pack.payments ?? []),
      }));

      const broken = schedules.find((entry) => entry.schedule.error);
      if (broken) {
        throw new Error(
          broken.name
            ? `${broken.name}: ${broken.schedule.error}`
            : broken.schedule.error!,
        );
      }

      // A payment plan the proposal would leave off, telling the customer
      // the Final is one payment while the job is on a plan (ADR 0286).
      const unprintedPlan = planWontPrint(document);
      if (unprintedPlan) throw new Error(unprintedPlan);

      return {
        document,
        leaks,
        /*
         * Whether the preview offers the Key Differentiator box for this
         * estimate. Asked of the packages the document was just built from,
         * through the rule the document itself applies, so the box is there
         * exactly when the customer's copy would print the line.
         */
        // Roof options only, whatever else the document sells (ADR 0124).
        keyLines: keyDifferentiatorPackages(quoted),
        items: fetchedItems,
        // In the main-estimate order, not the reading order, so the
        // proposal's options agree with the document about which one leads.
        estimateIds: packages.length > 0 ? leadOrder : [estimate.id],
      };
    }
  };

  /**
   * Put the typed lines on an estimate, replacing whatever was there.
   *
   * Replace rather than merge: the box the rep is looking at *is* the list, so
   * a line they deleted from it has to leave the estimate too. Only this
   * section is touched — every other line on the estimate is somebody else's
   * business.
   */
  const writeHomeItems = async (estimateId: string, lines: string[]) => {
    // Every spelling of the block, not the one this app writes today. A row
    // stored under "Items Specific to Your Home" — or under the commercial
    // wording, which the label above this box has shown since 2026-08-26 —
    // is the same block, and an exact match against one spelling would leave
    // it behind and print the rep's list twice.
    const existing = (await fetchEstimateChildren(estimateId)).items.filter(
      (item) => isHomeSpecificSection(item.section),
    );
    for (const row of existing) await deleteRow("estimate_items", row.id);

    let sort = HOME_ITEM_SORT_START;
    for (const line of lines) {
      await insertRow("estimate_items", {
        estimate_id: estimateId,
        description: line,
        section: HOME_SPECIFIC_SECTION,
        qty: 1,
        // No price: this block says what we are doing about their house, and
        // the package's own price already covers it. A line that carried money
        // would be a second price on a document that shows one.
        customer_price: 0,
        discount: 0,
        work_doing: true,
        customer_visible: true,
        sort_order: sort,
      });
      sort += 10;
    }
  };

  /**
   * Save the box, once the rep leaves it.
   *
   * The question is asked here rather than assumed either way, because both
   * answers are ordinary: a rotten fascia is true of the house whichever
   * package they buy, and a line about the metal panels is not.
   */
  const homeSavedFor = (estimateId: string): string =>
    homeWritten[estimateId] ?? homeText(rowsOf(estimateId));
  const saveHomeItems = async (estimateId: string) => {
    const typed = homeDraft[estimateId];
    if (typed === undefined || typed === homeSavedFor(estimateId)) return;
    const lines = splitLines(typed);

    const others = offeredEstimates.filter(
      (option) => option.id !== estimateId,
    );
    const everywhere =
      others.length > 0 &&
      window.confirm(
        tn(
          lines.length,
          "Put these 1 item under every package on this proposal?",
          "Put these {n} items under every package on this proposal?",
        ) +
          "\n\n" +
          t(
            "OK adds them to all {count}. Cancel keeps them on this estimate only.",
            {
              count: offeredEstimates.length,
            },
          ),
      );

    setBusy(true);
    setError(null);
    try {
      await writeHomeItems(estimateId, lines);
      const written = [estimateId];
      if (everywhere) {
        for (const option of others) {
          await writeHomeItems(option.id, lines);
          written.push(option.id);
        }
      }
      setHomeWritten((prior) => ({
        ...prior,
        ...Object.fromEntries(written.map((id) => [id, typed])),
      }));
      setHomeDraft((prior) => {
        const next = { ...prior };
        for (const id of written) delete next[id];
        return next;
      });
      // What they typed is on the estimates now, so the document they are
      // looking at is out of date until it is built again.
      requestRebuild();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /*
   * Whether Email it can go, and what to say when it cannot.
   *
   * Lysa could not email an estimate from the present screen on 2026-09-08 —
   * the button was simply grey, with her Gmail connected and sending fine. Two
   * things were wrong and the second is what made it baffling.
   *
   * **It asked the wrong question.** The gate was her OWN connected mailbox.
   * But an admin may send from anybody's connected mailbox — Jonathon asked for
   * that on 2026-08-24 and the picker inside the composer already offers it —
   * so the question is whether there is any mailbox she may send from, which
   * is exactly what `senders` answers.
   *
   * **It said nothing.** "Still loading", "we could not read it" and "nobody
   * has connected a mailbox" all rendered as one dead button. They need
   * different sentences, because only the last one is something a person can
   * go and fix.
   */
  const mailboxes = senders ?? [];
  const canEmail = mailboxes.length > 0 || Boolean(gmailAddress);
  const whyNoEmail = canEmail
    ? null
    : gmailLoading
      ? t("Still checking which mailboxes you can send from.")
      : gmailError
        ? t("Roof Board could not read the connected mailboxes: {error}", {
            error: gmailError,
          })
        : t(
            "No Google account is connected to send from. Connect yours under Tools → Integrations, then reopen this screen.",
          );

  /** Show the rep what they built. Nothing is written and nothing is locked. */
  const preview = async (over: { hiddenColors?: readonly string[] } = {}) => {
    setBusy(true);
    setError(null);
    try {
      const {
        document,
        leaks: found,
        keyLines,
        items: fetched,
      } = await buildDocument(over);
      setLeaks(found);
      setKeyLineIds(keyLines);
      setPackageItems(fetched);
      // A package no longer in the packet has nothing on the page to edit.
      if (!fetched.has(editingId)) setEditingId(estimate.id);
      // A fresh document is a fresh decision.
      setSendAnyway(false);
      /*
       * The photographs are the one thing that cannot be shown the way the
       * customer will see them: the document carries storage paths, and the
       * Edge Function that signs them only runs for a proposal that exists. So
       * they are signed here for the rep instead — same photographs, different
       * signature.
       */
      const findings = document.sections.find(
        (section): section is Extract<DocumentSection, { id: "findings" }> =>
          section.id === "findings",
      );
      if (findings && findings.photos.length > 0) {
        const urls = await signJobPhotos(
          findings.photos.map((photo) => photo.url),
        );
        findings.photos = findings.photos.flatMap((photo) =>
          urls[photo.url] ? [{ ...photo, url: urls[photo.url]! }] : [],
        );
      }
      setPreviewDoc(document);
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Hand it over, which is the moment it locks.
   *
   * Jonathon, 2026-08-17: "I don't want the lock to happen when clicking on
   * present. I want it to happen when it's actually been emailed or texted to
   * the customer." So the proposal — the frozen snapshot, the live link, the
   * revision that kills the last one — is written here, when the rep chooses a
   * way to deliver it, and never by looking at it.
   *
   * Written once. A rep who copies the link and then emails it has sent one
   * document, not two, so the second press reuses the first link rather than
   * quietly voiding it.
   */
  const deliver = async (how: "link" | "email" | "in_person") => {
    if (link) return link;
    setBusy(true);
    setError(null);
    try {
      const { document, estimateIds, leaks: found } = await buildDocument();
      // Belt and braces. The buttons are disabled until the box is ticked, so
      // this is only reachable if the document changed under the preview — and
      // an unapproved square count must not reach a customer by accident.
      if (found.length > 0 && !sendAnyway) {
        throw new Error(
          t("{warning} Tick the box above to send it anyway.", {
            warning: squareCountWarning(found),
          }),
        );
      }
      const proposal = await presentEstimate({
        jobId: job.id,
        estimateIds,
        snapshot: document,
        // The label core stamped on the document itself, so the stored version
        // and the snapshot can never name two different agreements (a deck
        // alone signs its own, ADR 0281).
        contractVersion: document.contractVersion,
        title: `Estimate ${job.job_number}`,
        delivery: how === "in_person" ? "in_person" : "email",
        recipientName: recipientName.trim() || null,
        recipientEmail: recipientEmail.trim() || null,
        recipientPhone: recipientPhone.trim() || null,
      });

      const url = `${window.location.origin}/p/${proposal.token}`;
      setLink(url);
      setRevision(proposal.revision);
      setPreviewDoc(null);
      await onPresented();
      return url;
    } catch (caught) {
      setError((caught as Error).message);
      return null;
    } finally {
      setBusy(false);
    }
  };

  /*
   * What they built, before anybody outside the office sees it.
   *
   * Over the build screen rather than replacing it: closing this puts the rep
   * back where they were with everything still typed, which is what somebody
   * who has just spotted a wrong colour needs. The three buttons along the
   * bottom are the only things on this screen that write anything.
   */
  if (previewDoc) {
    // Everything that writes on this screen is held by the same answer, so
    // there is no route out of it that skips the question.
    const held = leaks.length > 0 && !sendAnyway;
    /*
     * The package whose words are on screen, and its lines grouped under the
     * blocks they print in. Lines without an id are not rows anybody can save
     * to, and the rep's own block has its one box above.
     */
    const pickable = documentPackages(previewDoc)
      .filter(
        (pkg) =>
          pkg.estimateId === estimate.id || packageItems.has(pkg.estimateId),
      )
      .map((pkg) => ({ id: pkg.estimateId, name: pkg.name }));
    const editing =
      offeredEstimates.find((option) => option.id === editingId) ?? estimate;
    const editingBlocks = blocksById[editing.id] ?? documentBlocks(editing);
    // The line a blank Key Differentiator reads on this package, or null when
    // blank leaves it off: core's answer, from the words the preview built,
    // so a labor only roof or one naming no shingle is never promised one
    // (ADR 0318).
    const editingPackage = documentPackages(previewDoc).find(
      (pkg) => pkg.estimateId === editing.id,
    );
    const editingShingleLine = editingPackage
      ? packageShingleDifferentiator({
          trade: editing.trade,
          roofType: editing.roof_type,
          name: editingPackage.name,
          blocks: editingPackage.blocks,
        })
      : null;
    const editingLines = withEdits(editing.id, rowsOf(editing.id));
    const editingIsReplacement = isReplacementTrade(editing.trade);
    const labelOf = new Map(
      rowsOf(editing.id).map((line) => [line.id, line.description ?? ""]),
    );
    const linesByBlock = new Map<string, EstimateItem[]>();
    for (const line of editingLines) {
      if (!line.id || line.id.startsWith("typed-")) continue;
      // Core's own print filter, asked as though the line were switched on,
      // so a line switched off keeps its editor and can be switched back on.
      const switchedOn = { ...line, customer_visible: true };
      if (!printsInPackage(switchedOn, { homeSpecific: false })) continue;
      const key = sectionKey(line.section);
      linesByBlock.set(key, [...(linesByBlock.get(key) ?? []), line]);
    }
    const pagesHere = FIXED_PAGES.filter(
      (page) =>
        pagesOff.includes(page.id) ||
        previewDoc.sections.some((section) => section.id === page.id),
    );
    // The chart's columns as this document prints them, for the boxes below.
    const compareColumns =
      previewDoc.sections.find(
        (section): section is Extract<DocumentSection, { id: "compare" }> =>
          section.id === "compare",
      )?.packages ?? [];
    const heldWhy = t(
      "This document shows the customer a square count. Read the box above and tick it to send it anyway.",
    );
    return (
      <Modal
        title={t("Preview")}
        subtitle={t("Job #{n} · nothing has been sent", {
          n: job.job_number ?? "",
        })}
        wide
        onClose={() => setPreviewDoc(null)}
        actions={
          <>
            <button type="button" onClick={() => setPreviewDoc(null)}>
              {t("Back to edit")}
            </button>
            <button
              type="button"
              disabled={busy || held}
              title={held ? heldWhy : undefined}
              onClick={() =>
                void deliver("link").then((url) => {
                  if (url) void navigator.clipboard?.writeText(url);
                })
              }
            >
              {t("Copy link")}
            </button>
            <button
              type="button"
              disabled={busy || held || !canEmail}
              title={held ? heldWhy : (whyNoEmail ?? undefined)}
              onClick={() =>
                void deliver("email").then((url) => {
                  if (url) setEmailing(true);
                })
              }
            >
              {t("Email it")}
            </button>
            <button
              type="button"
              className="primary"
              disabled={busy || held}
              title={held ? heldWhy : undefined}
              onClick={() =>
                void deliver("in_person").then((url) => {
                  if (url) window.location.assign(url);
                })
              }
            >
              {t("Sign on this device")}
            </button>
          </>
        }
      >
        {error ? <ErrorNote error={error} /> : null}

        {/* A square count on the customer's copy. It refused outright until
            2026-09-08; Jonathon was blocked at a doorstep by a 1.69-square
            repair and asked for a warning he can overrule. It names what it
            found and where, because approving it without either is a decision
            made blind — and the old message sent people to the line items
            whether that is where it was or not. */}
        {leaks.length > 0 && (
          <div className="err" role="alert">
            <strong>{t("Squares are showing.")}</strong>{" "}
            {squareCountWarning(leaks)}
            <label style={{ display: "block", marginTop: 8 }}>
              <input
                type="checkbox"
                checked={sendAnyway}
                onChange={(e) => setSendAnyway(e.target.checked)}
              />{" "}
              {t(
                "Send it anyway — I have read it and I am happy for the customer to see this.",
              )}
            </label>
          </div>
        )}

        <div className="config-warn" role="status">
          <strong>{t("This is what the customer will see.")}</strong>{" "}
          {t(
            "Nothing has been sent and no link exists yet. The customer's copy is created — and locked — the moment you copy the link, email it, or hand them this device.",
          )}
        </div>

        {/* Two roofs the customer can buy together, off the document about to
            go. Allowed when a person set the box (ADR 0124) — a house and a
            garage — but the box looks the same when it was re-ticked over a
            default, and then it is two alternatives for one house that can
            both be bought. Said here, where the rep can still go back. */}
        {documentSellsSeveralRoofs(previewDoc) && (
          <div className="config-warn" role="status">
            <strong>{t("More than one roof can be bought.")}</strong>{" "}
            {t(ROOFS_TOGETHER_NOTICE)}
          </div>
        )}

        {/* Why Email it is grey, said where somebody will read it. A tooltip
            is not an answer on a phone, and a phone is where this screen is
            used. Copy link and Sign on this device still work, so this is a
            note rather than a warning. */}
        {whyNoEmail && (
          <div className="na-hint" role="status">
            <strong>{t("Email it is off.")}</strong>{" "}
            {t("{why} Copy link and Sign on this device still work.", {
              why: whyNoEmail,
            })}
          </div>
        )}

        {/* The pages that read the same on every proposal. Jonathon,
            2026-10-20: anything in writing outside the contract has to be
            removable here (ADR 0294). Only the ones this document carries,
            or one already switched off so it can be switched back on. */}
        {pagesHere.length > 0 && (
          <div className="pblocks">
            <div className="chk-sub">{t("Pages on this proposal")}</div>
            {pagesHere.map((page) => {
              const off = pagesOff.includes(page.id);
              return (
                <div className="togline" key={page.id}>
                  <button
                    className={`tog green${off ? "" : " on"}`}
                    aria-label={t(page.label)}
                    aria-pressed={!off}
                    disabled={busy}
                    title={
                      off
                        ? t('Put "{name}" back on the customer\'s copy', {
                            name: t(page.label),
                          })
                        : t('Leave "{name}" off the customer\'s copy', {
                            name: t(page.label),
                          })
                    }
                    onClick={() => {
                      setPagesOff((was) =>
                        off
                          ? was.filter((id) => id !== page.id)
                          : [...was, page.id],
                      );
                      requestRebuild();
                    }}
                  />
                  <span className={off ? "na-hint" : undefined}>
                    {t(page.label)}
                  </span>
                </div>
              );
            })}
            <p className="n">
              {t(
                "Off leaves the page out of this proposal only. The next one starts with every page on.",
              )}
            </p>
          </div>
        )}

        {/* Which package's words are below. Every package in the packet,
            not only the estimate this screen was opened from — Jonathon,
            2026-10-20: "i need to able to remove the what you get from any
            of the packages when reviewing what's presented." */}
        {pickable.length > 1 && (
          <>
            <div className="chk-sub">{t("Edit the words of")}</div>
            <div className="pkgpick">
              {pickable.map((pkg) => (
                <button
                  key={pkg.id}
                  type="button"
                  className={pkg.id === editing.id ? "primary" : undefined}
                  aria-label={t("Edit the words of {name}", { name: pkg.name })}
                  aria-pressed={pkg.id === editing.id}
                  disabled={busy}
                  onClick={() => setEditingId(pkg.id)}
                >
                  {pkg.name}
                </button>
              ))}
            </div>
          </>
        )}

        {/* The package's name, as the customer reads it above its price.
            The same name the builder edits; blank reads the first line. */}
        <Row label={t("Package name")}>
          <input
            aria-label={t("Package name")}
            value={nameDraft[editing.id] ?? editingBlocks.name ?? ""}
            placeholder={
              pickable.find((pkg) => pkg.id === editing.id)?.name ?? undefined
            }
            disabled={busy}
            onChange={(e) =>
              setNameDraft((was) => ({ ...was, [editing.id]: e.target.value }))
            }
            onBlur={() => {
              const id = editing.id;
              const typed = nameDraft[id];
              if (typed === undefined) return;
              // Kept until the save lands, so a failed one loses nothing.
              const dropDraft = () =>
                setNameDraft(({ [id]: _left, ...rest }) => rest);
              const next = typed.trim() || null;
              if (next === (editingBlocks.name ?? null)) return dropDraft();
              void saveBlocks(id, { name: next }).then(
                (saved) => saved && dropDraft(),
              );
            }}
          />
        </Row>

        {/* The line under the package name on the customer's copy. Seeded
            from the template and edited here, on the estimate, so changing it
            for one customer does not change it for the next one.

            Shown on a roof option when this proposal is pitching more than
            one whole roof, which is the only time the line has a job to do.
            Jonathon, 2026-09-08: "Key differentiators are only for when
            you're pitching multiple replacement estimates", and 2026-09-10:
            "It should print on roof options only" — never the gutters, the
            siding or a deck, and whether or not the document sells several
            things (ADR 0124).

            Not counted here. The preview is drawn from a built document, and
            this is the answer that document's own packages gave
            (`keyDifferentiatorPackages` in core) — so the box and the copy
            count the same roofs, and the line is never printed with no box
            on the screen to change it. */}
        {keyLineIds.includes(editing.id) ? (
          <>
            <Row label={t("Key Differentiator")}>
              <input
                aria-label={t("Key Differentiator")}
                value={
                  keyDraft[editing.id] ?? editingBlocks.key_differentiator ?? ""
                }
                disabled={busy}
                placeholder={
                  // On new construction, blank reads the shingle's own line,
                  // which is what the document above prints (ADR 0318).
                  editingShingleLine ||
                  "30+ Year Life Expectancy and 30 Year Workmanship Warranty"
                }
                onChange={(e) =>
                  setKeyDraft((was) => ({
                    ...was,
                    [editing.id]: e.target.value,
                  }))
                }
                onBlur={() => {
                  const id = editing.id;
                  const typed = keyDraft[id];
                  if (typed === undefined) return;
                  // Kept until the save lands, so a failed one loses nothing.
                  const dropDraft = () =>
                    setKeyDraft(({ [id]: _left, ...rest }) => rest);
                  const next = typed.trim() || null;
                  if (next === (editingBlocks.key_differentiator ?? null))
                    return dropDraft();
                  void saveBlocks(id, { key_differentiator: next }).then(
                    (saved) => saved && dropDraft(),
                  );
                }}
              />
            </Row>
            <p className="na-hint">
              {t(
                "The one line the customer reads under the package name, telling them why this package rather than the other.",
              )}{" "}
              {editingShingleLine
                ? t('Blank reads the shingle\'s own: "{line}".', {
                    line: editingShingleLine,
                  })
                : t("Blank leaves it off.")}
            </p>
          </>
        ) : null}

        {/* Everything else the customer reads in this package: which blocks,
            in what order, in whose words — and, inside a block, each line's
            own words and its own switch. Jonathon asked for one thing —
            remove it, move it, edit it (2026-08-27) — and on 2026-10-20 for
            it line by line: switching off Scope of Work to lose What You Get
            took the shingle line's scope of work with it. */}
        <ProposalBlocks
          key={editing.id}
          jobType={job.job_type}
          estimate={editingBlocks}
          items={editingLines}
          onSave={async (patch) => {
            await saveBlocks(editing.id, patch);
          }}
          disabled={busy}
          alsoOffer={
            editingIsReplacement
              ? [
                  {
                    key: HOME_SPECIFIC_KEY,
                    heading: homeSpecificHeading(job.job_type),
                  },
                ]
              : []
          }
          editors={{
            /* The one block a rep writes themselves. Everything else on a
               package is the same wording every time; this is what makes the
               proposal about the house they are standing in front of.

               Replacements only. Jonathon, 2026-08-19: "this does not belong
               on estimates created from scratch, or repair estimates. only on
               replacement estimates." A repair is already described in its own
               words — the scope of work is the whole line item — and an
               estimate with no trade on it is half-built, not a package. The
               test is the trade, the same one that chooses the contract
               (ADR 0037). */
            ...(editingIsReplacement
              ? {
                  [HOME_SPECIFIC_KEY]: (
                    <>
                      <GrowBox
                        className="pdoc-home-items"
                        rows={4}
                        value={
                          homeDraft[editing.id] ?? homeSavedFor(editing.id)
                        }
                        disabled={busy}
                        onChange={(e) =>
                          setHomeDraft((was) => ({
                            ...was,
                            [editing.id]: e.target.value,
                          }))
                        }
                        onBlur={() => void saveHomeItems(editing.id)}
                        placeholder={
                          t("One per line — e.g.") +
                          "\n" +
                          "Replace rotten fascia over the garage\n" +
                          "Take down and re-set the satellite dish"
                        }
                      />
                      <p className="na-hint">
                        {t(
                          "One per line. Leaving the box asks whether they belong under every package or only this one.",
                        )}
                      </p>
                    </>
                  ),
                }
              : {}),
            /* Every other line, under the block it prints in. A wording line
               — What You Get, Warranty, the wood bonus — has its own switch;
               a priced line's scope can be retyped or emptied, and the line
               and its price stay on the estimate either way. */
            ...Object.fromEntries(
              [...linesByBlock].map(([key, lines]) => [
                key,
                <>
                  {lines.map((line) => {
                    const label =
                      labelOf.get(line.id) ?? line.description ?? "";
                    const wording = isWordingLine(line);
                    const off = line.customer_visible === false;
                    return (
                      <div className="pline" key={line.id}>
                        <div className="togline">
                          {wording ? (
                            <button
                              className={`tog green${off ? "" : " on"}`}
                              aria-label={t("Show {name}", { name: label })}
                              aria-pressed={!off}
                              disabled={busy}
                              title={
                                off
                                  ? t(
                                      'Put "{name}" back on the customer\'s copy',
                                      {
                                        name: label,
                                      },
                                    )
                                  : t(
                                      'Leave "{name}" off the customer\'s copy',
                                      {
                                        name: label,
                                      },
                                    )
                              }
                              onClick={() =>
                                void saveLine(line.id, {
                                  customer_visible: off,
                                })
                              }
                            />
                          ) : null}
                          <input
                            className="pline-name"
                            aria-label={t("Name of {name}", { name: label })}
                            value={
                              lineDrafts[`${line.id}:description`] ??
                              line.description ??
                              ""
                            }
                            disabled={busy}
                            onChange={(e) =>
                              setLineDrafts((was) => ({
                                ...was,
                                [`${line.id}:description`]: e.target.value,
                              }))
                            }
                            onBlur={() => leaveLineBox(line, "description")}
                          />
                        </div>
                        <GrowBox
                          className="pdoc-home-items"
                          rows={wording ? 5 : 3}
                          aria-label={label}
                          value={
                            lineDrafts[`${line.id}:note`] ?? line.note ?? ""
                          }
                          disabled={busy}
                          placeholder={
                            wording
                              ? undefined
                              : t(
                                  "No scope of work — the customer reads the name only",
                                )
                          }
                          onChange={(e) =>
                            setLineDrafts((was) => ({
                              ...was,
                              [`${line.id}:note`]: e.target.value,
                            }))
                          }
                          onBlur={() => leaveLineBox(line, "note")}
                        />
                      </div>
                    );
                  })}
                </>,
              ]),
            ),
          }}
        />
        <p className="na-hint">
          {t(
            "Each package carries its own words, its own order and its own key differentiator. Every box saves onto the estimate when you leave it; a priced line's scope is the same one the builder shows.",
          )}
        </p>

        {/* The comparison chart, line by line. It fills itself in from each
            package (ADR 0274), and from 2026-10-27 the rep can retype, add or
            take away any line of a column, saved on that estimate as the
            package's other words are (ADR 0318). Jonathon: "everything on
            the present screen should be fully custamizeable and user
            friendly". What is saved is the changes, not the column, so a
            line the rep did not touch keeps following the package
            (2026-10-30, "13. b"). */}
        {compareColumns.length > 0 && (
          <div className="pblocks cmp-lines">
            <div className="chk-sub">Comparison chart</div>
            {compareColumns.map((column) => {
              const id = column.estimateId;
              const own =
                (
                  blocksById[id] ??
                  documentBlocks(
                    estimates.find((option) => option.id === id) ?? estimate,
                  )
                ).compare_edits ?? null;
              // The saved changes before the preview's: a save lands in
              // `blocksById` at once, and the rebuilt preview only later, so
              // the next edit in between builds on what was just saved rather
              // than writing the old changes back.
              const edits = editDrafts[id] ?? own;
              const rows = chartRows(
                column.packagePoints ?? column.points,
                edits,
              );
              const dropDraft = () =>
                setEditDrafts(({ [id]: _left, ...rest }) => rest);
              const save = (next: ChartEdits | null) => {
                if (next) setEditDrafts((was) => ({ ...was, [id]: next }));
                void saveBlocks(id, {
                  compare_edits: tidyChartEdits(next),
                }).then((saved) => saved && dropDraft());
              };
              return (
                <div className="pblock" key={id}>
                  <strong>{column.name}</strong>
                  {rows.map((row, index) => (
                    <div
                      className="togline"
                      key={
                        row.from === "package"
                          ? `package:${row.line}`
                          : `added:${row.index}`
                      }
                    >
                      <input
                        aria-label={`${column.name} chart line ${index + 1}`}
                        value={row.text}
                        disabled={busy}
                        onChange={(e) =>
                          setEditDrafts((was) => ({
                            ...was,
                            [id]: retypeChartRow(edits, row, e.target.value),
                          }))
                        }
                        onBlur={() => {
                          const draft = editDrafts[id];
                          if (draft) save(draft);
                        }}
                      />
                      <button
                        type="button"
                        className="cmp-line-x"
                        aria-label={`Remove "${row.text || `line ${index + 1}`}" from ${column.name}`}
                        disabled={busy}
                        onClick={() => save(removeChartRow(edits, row))}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <div className="cmp-line-actions">
                    <button
                      type="button"
                      disabled={busy}
                      aria-label={`Add a line to ${column.name}`}
                      onClick={() =>
                        setEditDrafts((was) => ({
                          ...was,
                          [id]: addChartRow(edits),
                        }))
                      }
                    >
                      + Add a line
                    </button>
                    {own ? (
                      <button
                        type="button"
                        disabled={busy}
                        aria-label={`Back to the package's own lines for ${column.name}`}
                        onClick={() => {
                          dropDraft();
                          void saveBlocks(id, { compare_edits: null });
                        }}
                      >
                        Back to the package's own
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
            <p className="na-hint">
              The lines the customer reads in each roof's column. They fill
              themselves in from what the package says; retype, add or remove
              any, and the change saves onto that estimate when you leave the
              box. A line you have not touched keeps following the package.
            </p>
          </div>
        )}
        <PresentedDocumentView
          document={previewDoc}
          companySignedAt={null}
          onSign={() => undefined}
          preview
        />
      </Modal>
    );
  }

  if (link) {
    return (
      <Modal title={t("Ready to sign")} onClose={onClose}>
        <p>
          {t(
            "The document is locked and the link is live for 30 days. Editing this job from now on will not change what the customer sees — to change their copy, present it again, which replaces this one.",
          )}
        </p>
        {revision > 1 ? (
          <p className="n">
            {t(
              "This is revision {n}. Any link sent to this customer before now has stopped working, so there is only ever one document they can sign.",
              { n: revision },
            )}
          </p>
        ) : null}
        <Row label={t("Link")}>
          <input readOnly value={link} onFocus={(e) => e.target.select()} />
        </Row>
        <div className="mact">
          <button
            type="button"
            onClick={() => void navigator.clipboard?.writeText(link)}
          >
            {t("Copy link")}
          </button>
          <button
            type="button"
            disabled={!canEmail}
            title={whyNoEmail ?? undefined}
            onClick={() => setEmailing(true)}
          >
            {t("Email the link")}
          </button>
          <button
            type="button"
            className="primary"
            onClick={() => window.location.assign(link)}
          >
            {/* The same words as the button on the screen before this one.
                Both open the customer's copy on the rep's own device; calling
                one of them "Sign now" made them read as two different things. */}
            {t("Sign on this device")}
          </button>
        </div>
        <p className="n">
          {canEmail
            ? t(
                "Email it below, or paste the link wherever you already talk to this customer. Texting is still by hand.",
              )
            : t(
                "{why} Until then, paste the link wherever you already talk to this customer.",
                { why: whyNoEmail ?? "" },
              )}
        </p>
        {emailing && (
          <ComposeEmail
            title={t("Email the estimate to {name}", {
              name: recipientName || t("the customer"),
            })}
            subtitle={t("They can read and sign it on their phone.")}
            to={recipientEmail || (job.contact?.email ?? "")}
            subject={`Your estimate from ${company?.name ?? "us"} — ${jobAddress(job)}`}
            body={[
              `Hi ${(recipientName || job.contact?.full_name || "").split(" ")[0]},`,
              "",
              "Here is your estimate. You can read it and sign it from your phone — no printing, no scanning:",
              "",
              link,
              "",
              "The link works for 30 days. Give me a call if anything on it needs changing.",
              "",
              "Thanks,",
              repName ?? "",
            ]
              .filter((line) => line !== null)
              .join("\n")}
            contact={job.contact}
            contactId={job.contact_id}
            jobId={job.id}
            connectedAs={gmailAddress ?? null}
            senders={senders}
            ownSender={ownSender ?? null}
            sendLabel={t("Send the estimate")}
            onClose={() => setEmailing(false)}
          />
        )}
      </Modal>
    );
  }

  return (
    <Modal
      title={t("Present Estimate")}
      subtitle={t("Job #{n} · {name}", {
        n: job.job_number ?? "",
        name: job.contact?.full_name ?? "",
      })}
      onClose={onClose}
      // Jonathon, 2026-08-24: "the present estimate pop up can be wider and
      // easier to read." It is a checklist of everything going out the door —
      // the packages, the pages, the upgrades, who it is for — and at 560px
      // every one of those rows wrapped. `roomy` rather than `wide`: this is a
      // column of rows to read, not a table to fit.
      roomy
      actions={
        <>
          <button type="button" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="primary"
            disabled={busy}
            onClick={() => void preview()}
          >
            {busy ? t("Preparing…") : t("Present")}
          </button>
        </>
      }
    >
      {error ? <ErrorNote error={error} /> : null}

      {/* A warning, not a gate, and a much shorter one than it was.
          Jonathon, 2026-08-24: the paragraph about filling the shingle and the
          colour in behind this screen "does not need to be on the pop up". It
          does not, because those boxes no longer exist — what is left is the
          company's own details and the customer's name and address, which are
          the only tokens the contract still carries, and each is named with
          the one place it is fixed. */}
      {gaps.length > 0 && (
        <div className="config-warn" role="status">
          <strong>
            {tn(
              gaps.length,
              "One blank will print on the contract",
              "{n} blanks will print on the contract",
            )}
          </strong>{" "}
          {theirGaps.length > 0 && (
            <>
              {t("Fill {fields} in on the job.", {
                fields: theirGaps.join(", "),
              })}{" "}
            </>
          )}
          {ourGaps.length > 0 && (
            <>
              {t("Fill {fields} in under Tools → Emails → Company details.", {
                fields: ourGaps.join(", "),
              })}{" "}
            </>
          )}
          {t("You can present without them.")}
        </div>
      )}

      {/* What is going out, ticked one by one.

          One estimate is a price; two or more is a choice the customer makes on
          their own phone, and the one they pick is the one approved when they
          sign. Anything left unticked is not on the document at all. */}
      {candidates.length > 1 && (
        <>
          <div className="chk-sub">{t("Present")}</div>
          {ordered.map((option, at) => {
            const isThisOne = option.id === estimate.id;
            const label =
              optionLabels.get(option.id) ??
              estimateOptionLabel(option, lineNames.data?.get(option.id));
            const total = optionTotals.data?.get(option.id);
            return (
              <Row key={option.id} label={label}>
                <label className="inline">
                  <input
                    type="checkbox"
                    checked={isThisOne || alsoPresent.includes(option.id)}
                    disabled={isThisOne}
                    onChange={(e) =>
                      setAlsoPresent((was) =>
                        e.target.checked
                          ? [...was, option.id]
                          : was.filter((id) => id !== option.id),
                      )
                    }
                  />
                  <span>
                    {isThisOne
                      ? t("The estimate you are presenting")
                      : t("Present this one too")}
                  </span>
                </label>
                {total != null ? (
                  <span className="na-hint">{formatMoney(total)}</span>
                ) : null}
                {/* The order the customer reads them in. Most expensive
                    first until an arrow is pressed (ADR 0294). */}
                <span className="pblock-move">
                  <button
                    type="button"
                    className="ghost"
                    aria-label={t('Move "{name}" up', { name: label })}
                    disabled={at === 0}
                    onClick={() => moveOption(at, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="ghost"
                    aria-label={t('Move "{name}" down', { name: label })}
                    disabled={at === ordered.length - 1}
                    onClick={() => moveOption(at, 1)}
                  >
                    ↓
                  </button>
                </span>
              </Row>
            );
          })}
          <p className="n">
            {t(
              "The customer reads them top to bottom: most expensive first, until you move one.",
            )}
          </p>
          {/* What the customer can do with them: pick one, buy several, or
              buy several roofs. Worded in core from the same three answers
              the document is built from. */}
          <p className="n">
            {packetSummary(
              multiOptionNow,
              multiOptionManualNow,
              offeredRoofOptions,
            )}
          </p>
          {sellsSeveralRoofs(
            multiOptionNow,
            multiOptionManualNow,
            offeredRoofOptions,
          ) && (
            <div className="config-warn" role="status">
              <strong>{t("More than one roof can be bought.")}</strong>{" "}
              {t(ROOFS_TOGETHER_NOTICE)}
            </div>
          )}
        </>
      )}

      {/* Pages that are not always wanted. Off here means absent from the
          document, not hidden in it — nothing downstream has to remember. */}
      <div className="chk-sub">{t("Pages to include")}</div>
      <Row label={t("Financing")}>
        <label className="inline">
          <input
            type="checkbox"
            checked={financingPage}
            onChange={(e) => setFinancingPage(e.target.checked)}
          />
          <span>
            {financingPage
              ? t("The financing page goes out with this")
              : t("No financing page on this proposal")}
          </span>
        </label>
      </Row>
      <p className="n">
        {t(
          "Starts as the estimate's own Show financing setting. Off means the page is not in the customer's copy at all; the monthly figure beside each price is a separate thing and stays.",
        )}
      </p>
      {/* Only where there is a chart to switch off: `documentComparesRoofs`. */}
      {showCompareSwitch && (
        <Row label={t("Comparison chart")}>
          <label className="inline">
            <input
              type="checkbox"
              checked={comparePage}
              onChange={(e) => setComparePage(e.target.checked)}
            />
            <span>
              {comparePage
                ? t("The roofs are compared side by side")
                : t("No comparison chart on this proposal")}
            </span>
          </label>
        </Row>
      )}
      {/* The colour cards, one switch each. Off means the card — and, with
          every card off, the page — is absent from the document. Saved on the
          estimate, so a repair with no colour to choose stays that way. */}
      <div className="chk-sub">{t("Color options")}</div>
      {colorCardsOffered.loading ? (
        <p className="n">
          {t("Working out which color options this carries…")}
        </p>
      ) : (colorCardsOffered.data ?? []).length === 0 ? (
        <p className="n">
          {t(
            "Nothing on this proposal comes in a choice of colors, so there is no color options page.",
          )}
        </p>
      ) : (
        <div className="pblocks">
          {(colorCardsOffered.data ?? []).map((card) => {
            const off = hiddenColors.includes(card.key);
            const label = `${card.name} — ${card.product}`;
            return (
              <div className="togline" key={card.key}>
                <button
                  className={`tog green${off ? "" : " on"}`}
                  aria-label={t("{product} colors", { product: card.product })}
                  aria-pressed={!off}
                  disabled={busy}
                  title={
                    off
                      ? t(
                          "Put the {product} colors back on the customer's copy",
                          {
                            product: card.product,
                          },
                        )
                      : t(
                          "Leave the {product} colors off the customer's copy",
                          {
                            product: card.product,
                          },
                        )
                  }
                  onClick={() => void toggleColorCard(card.key)}
                />
                <span className={off ? "na-hint" : undefined}>{label}</span>
              </div>
            );
          })}
          <p className="n">
            {t(
              "Off when nobody is choosing that color — gutters matched to the trim, a repair matching what is on the roof. Remembered on this estimate. With every one off, the page is left out.",
            )}
          </p>
        </div>
      )}

      {/* The upgrades, priced for this roof. Ticked off here to keep one off
          this proposal — the product stays on offer for the next customer. */}
      {offerableUpgrades.length > 0 && (
        <>
          <div className="chk-sub">{t("Upgrades to offer")}</div>
          {offerableUpgrades.map((product) => {
            const measured = priceUpgrade(product, layouts);
            const priced = priceUpgrade(
              product,
              layouts,
              typedUpgradeQty(product.name),
            );
            const dropped = droppedUpgrades.includes(product.name);
            const includedIn = offeredEstimates.filter((option) =>
              (option.included_upgrades ?? []).includes(product.name),
            );
            return (
              <Row key={product.id} label={product.name}>
                <label className="inline">
                  <input
                    type="checkbox"
                    checked={!dropped && priced !== null}
                    disabled={priced === null}
                    onChange={(e) =>
                      setDroppedUpgrades((was) =>
                        e.target.checked
                          ? was.filter((name) => name !== product.name)
                          : [...was, product.name],
                      )
                    }
                  />
                  {priced === null ? (
                    <span>{t("No measurement to price it from")}</span>
                  ) : (
                    <span>{formatMoney(priced.price)}</span>
                  )}
                </label>
                {priced !== null && measured !== null ? (
                  <span className="upgradeqty">
                    <input
                      className="num"
                      inputMode="decimal"
                      aria-label={t("Quantity of {name}", {
                        name: product.name,
                      })}
                      placeholder={String(measured.qty)}
                      value={upgradeQty[product.name] ?? ""}
                      aria-invalid={upgradeQtyIgnored(product.name)}
                      disabled={dropped}
                      onChange={(e) =>
                        setUpgradeQty((was) => ({
                          ...was,
                          [product.name]: e.target.value,
                        }))
                      }
                    />
                    {product.unit ? <span>{product.unit}</span> : null}
                    {upgradeQtyIgnored(product.name) ? (
                      <span className="na-hint">
                        {t("Not a number — priced at {qty}", {
                          qty: measured.qty,
                        })}
                      </span>
                    ) : null}
                  </span>
                ) : null}
                {includedIn.length > 0 ? (
                  <span className="na-hint">
                    {t("Included in {names}", {
                      names: includedIn.map((o) => o.name ?? "").join(", "),
                    })}
                  </span>
                ) : null}
              </Row>
            );
          })}
          <p className="n">
            {t(
              "The customer ticks these under the packages. Anything already in a package shows as Included there and adds nothing to its price. Type over a quantity to change it on this proposal only.",
            )}
          </p>
        </>
      )}

      {/* Their own roof, on their own proposal. The photographs live on the
          job, so they survive a document being voided and re-presented. */}
      <ProposalPhotos jobId={job.id} />
      <ProposalDocuments estimateId={estimate.id} />

      <div className="chk-sub">{t("Who it is for")}</div>
      <Row label={t("Name")}>
        <input
          value={recipientName}
          onChange={(e) => setRecipientName(e.target.value)}
        />
      </Row>
      <Row label={t("Email")}>
        <input
          value={recipientEmail}
          onChange={(e) => setRecipientEmail(e.target.value)}
        />
      </Row>
      <Row label={t("Phone")}>
        <input
          value={recipientPhone}
          onChange={(e) => setRecipientPhone(e.target.value)}
        />
      </Row>
    </Modal>
  );
}
